import { useIntl } from 'react-intl';
import { Activity, LucideIcon } from 'lucide-react';
import { Button } from '@hqo/ui-atoms';
import { ActivitySegment } from 'app/data/types/activity';
import { ACTIVITY_TYPE_CONFIG, ActivityFilter } from 'app/shared/consts/activity';

/**
 * Concentric fading rings, scaled down from the `@hqo/unlayer-toolkit` PreviewEmptyState (690→130px)
 * to fit the narrow miniapp viewport (345→65px). Inner rings are the most opaque; each ring further
 * out fades toward the edge. Kept as literal class strings so Tailwind's source scan emits them.
 */
const RING_CLASSES = [
  'size-[386px] opacity-5',
  'size-[344px] opacity-[0.15]',
  'size-[302px] opacity-25',
  'size-[260px] opacity-[0.35]',
  'size-[218px] opacity-45',
  'size-[176px] opacity-[0.55]',
  'size-[134px] opacity-65',
  'size-[92px] opacity-70',
];

interface EmptyStateShellProps {
  icon: LucideIcon;
  /** Icon colour override (defaults to muted grey). */
  iconClassName?: string;
  title: string;
  body: string;
  ctaLabel?: string;
  onCta?: () => void;
  dataTestId?: string;
}

/**
 * The shared empty-screen visual (rings + icon card + title + body + optional CTA), modelled on the
 * unlayer preview empty state and scaled for the miniapp. Both the per-filter empty state and the
 * "caught up" state render this, so every empty screen reads as one design.
 */
export const EmptyStateShell = ({
  icon: Icon,
  iconClassName,
  title,
  body,
  ctaLabel,
  onCta,
  dataTestId = 'activity-empty-state',
}: EmptyStateShellProps): JSX.Element => (
  <div
    className="flex min-h-[360px] flex-col items-center justify-center gap-6 overflow-hidden px-5 text-center"
    data-testid={dataTestId}
  >
    <div className="flex flex-col items-center gap-5">
      <div className="relative flex items-center justify-center">
        {RING_CLASSES.map((ring) => (
          <div key={ring} className={`pointer-events-none absolute rounded-full border border-border ${ring}`} />
        ))}
        <div className="relative z-10 grid size-[48px] place-items-center rounded-xl border border-border bg-card shadow-sm">
          <Icon size={20} strokeWidth={1.75} className={iconClassName ?? 'text-muted-foreground'} aria-hidden />
        </div>
      </div>
      <div className="relative z-10 flex flex-col items-center gap-2">
        <h2 className="text-balance text-lg font-semibold leading-7 text-foreground">{title}</h2>
        <p className="max-w-[264px] text-sm leading-6 text-muted-foreground">{body}</p>
      </div>
    </div>
    {onCta && (
      <Button
        type="button"
        onClick={onCta}
        variant="default"
        className="relative z-10 h-auto whitespace-nowrap rounded-full px-[22px] py-3 text-sm font-semibold shadow-none"
        data-testid="activity-empty-state-cta"
      >
        {ctaLabel}
      </Button>
    )}
  </div>
);

interface ActivityEmptyStateProps {
  /** Which filter is active — picks the vertical-specific copy, or the all-verticals default. */
  filter: ActivityFilter;
  segment: ActivitySegment;
  onCta?: () => void;
  /**
   * The building's own icon for this vertical. Only the feed knows it — the empty state is also rendered
   * for `all`, which is no vertical at all — so it arrives as a prop and falls back to the built-in.
   */
  Icon?: LucideIcon;
}

interface EmptyCopy {
  /** The built-in icon. Overridden by the building's own for a real vertical; `all` has no vertical. */
  icon: LucideIcon;
  titleId: string;
  bodyId: string;
  ctaId: string;
}

const copyFor = (filter: ActivityFilter): EmptyCopy => {
  if (filter === 'all') {
    return {
      icon: Activity,
      titleId: 'activity.empty.all.title',
      bodyId: 'activity.empty.all.body',
      ctaId: 'activity.empty.all.cta',
    };
  }
  const config = ACTIVITY_TYPE_CONFIG[filter];
  return { icon: config.icon, titleId: config.emptyTitleId, bodyId: config.emptyBodyId, ctaId: config.emptyCtaId };
};

/** Shown whenever the active pane is empty — segment-aware title ("No upcoming …" / "No prior …")
 *  so an empty pane never claims you have none of a type when the OTHER pane has some. */
export const ActivityEmptyState = ({ filter, segment, onCta, Icon }: ActivityEmptyStateProps): JSX.Element => {
  const intl = useIntl();
  const { icon, titleId, bodyId, ctaId } = copyFor(filter);

  return (
    <EmptyStateShell
      icon={Icon ?? icon}
      title={intl.formatMessage({ id: `${titleId}.${segment}` })}
      body={intl.formatMessage({ id: bodyId })}
      ctaLabel={intl.formatMessage({ id: ctaId })}
      onCta={onCta}
    />
  );
};
